// ============================================================
// MLC watch-roster engine — daily-repeating 24h patterns.
//
// Regime: MINIMUM REST (MLC 2006 Std A2.3.5(b) / STCW A-VIII/1):
//   - >= 10h rest in any 24h period
//   - >= 77h rest in any 7-day period  (=> >= 11h/day if the
//     pattern repeats daily; 10h days are only legal if other
//     days compensate)
//   - rest divisible into max 2 periods, one >= 6h
//   - max 14h between consecutive rest periods
//
// For a daily-repeating pattern, any 24-consecutive-hour window
// contains each hour-of-day exactly once, so rest-in-any-24h
// equals the day's R count. Rest periods are counted CIRCULARLY
// (a block spanning midnight is one period).
// ============================================================

export const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0') + ':00')

// cells: 24-char string of 'W'/'R'
export function cellsToArray(cells) { return cells.split('') }
export function arrayToCells(arr) { return arr.join('') }

// Circular runs of 'R'. Returns [{ start, len }] in hours.
export function restPeriods(arr) {
  const n = 24
  if (arr.every(c => c === 'R')) return [{ start: 0, len: n }]
  if (arr.every(c => c === 'W')) return []
  const s = arr.indexOf('W')            // anchor on a work hour so runs never split at the wrap
  const runs = []
  let j = 0
  while (j < n) {
    const idx = (s + j) % n
    if (arr[idx] === 'R') {
      const start = idx
      let len = 0
      while (j < n && arr[(s + j) % n] === 'R') { len++; j++ }
      runs.push({ start, len })
    } else j++
  }
  return runs
}

export function longestWorkRun(arr) {
  const inverted = arr.map(c => (c === 'W' ? 'R' : 'W'))
  const runs = restPeriods(inverted)    // work runs, circularly
  return runs.length ? Math.max(...runs.map(r => r.len)) : 0
}

// Per-crew-row compliance. Returns { workHours, restHours, periods, issues: [{level, msg}] }
export function checkRow(cells) {
  const arr = cellsToArray(cells)
  const restHours = arr.filter(c => c === 'R').length
  const workHours = 24 - restHours
  const periods = restPeriods(arr)
  const maxRest = periods.length ? Math.max(...periods.map(p => p.len)) : 0
  const maxWork = longestWorkRun(arr)
  const issues = []
  if (restHours < 10) issues.push({ level: 'red', msg: `Only ${restHours}h rest — MLC minimum is 10h in any 24h` })
  else if (restHours < 11) issues.push({ level: 'amber', msg: `${restHours}h rest — breaches the 77h/7-day rule if this pattern runs all week (needs 11h/day)` })
  if (periods.length > 2) issues.push({ level: 'red', msg: `Rest split into ${periods.length} periods — MLC allows at most 2` })
  if (periods.length >= 1 && maxRest < 6) issues.push({ level: 'red', msg: `Longest rest period is ${maxRest}h — one period must be at least 6h` })
  if (maxWork > 14) issues.push({ level: 'red', msg: `${maxWork}h between rest periods — maximum is 14h` })
  return { workHours, restHours, periods, maxRest, maxWork, issues }
}

// Coverage: how many crew are on Work at each hour.
export function coverage(rows) {
  const counts = Array(24).fill(0)
  rows.forEach(r => cellsToArray(r.cells).forEach((c, h) => { if (c === 'W') counts[h]++ }))
  return counts
}

// Whole-schedule check.
// Coverage requirement can be a single number (same all day) or an array of 24
// per-hour minimums. Old saved schedules use the number form.
export function normaliseMin(minOnDeck, coverageWindow = null) {
  if (Array.isArray(minOnDeck) && minOnDeck.length === 24) return minOnDeck.map(n => Number(n) || 0)
  const n = Number(minOnDeck) || 0
  return Array.from({ length: 24 }, (_, h) =>
    (!coverageWindow || (h >= coverageWindow[0] && h < coverageWindow[1])) ? n : 0)
}

export function checkSchedule(rows, minOnDeck = 0, coverageWindow = null) {
  const perRow = rows.map(r => ({ ...r, check: checkRow(r.cells) }))
  const cov = coverage(rows)
  const req = normaliseMin(minOnDeck, coverageWindow)
  const lowHours = cov.map((c, h) => ({ h, c })).filter(x => req[x.h] > 0 && x.c < req[x.h]).map(x => x.h)
  const redCount = perRow.reduce((n, r) => n + r.check.issues.filter(i => i.level === 'red').length, 0)
  const amberCount = perRow.reduce((n, r) => n + r.check.issues.filter(i => i.level === 'amber').length, 0)
  return { perRow, coverage: cov, lowHours, redCount, amberCount, compliant: redCount === 0 }
}

// Feasibility: can `crewCount` crew legally provide `minOnDeck` coverage
// for `coverageHours` hours/day? Each crew supplies at most 13 legal work
// hours/day (11h rest floor for a repeating pattern).
export function feasibility(crewCount, minOnDeck, coverageWindow = null) {
  // minOnDeck may be a number or a 24-hour array. Demand is the sum of the
  // per-hour requirement across the day.
  const req = normaliseMin(minOnDeck, coverageWindow)
  const demand = req.reduce((a, b) => a + b, 0)
  const supply = crewCount * 13          // 13h is the legal daily maximum for a repeating pattern
  const peak = Math.max(0, ...req)
  const slack = supply - demand
  return {
    feasible: supply >= demand && crewCount >= peak,
    tight: slack >= 0 && slack <= 3,     // no room for a sick day or an overrun
    supply, demand, peak, slack,
    msg: crewCount < peak
      ? `${peak} crew must be on deck at once, but only ${crewCount} are on the roster.`
      : supply >= demand
        ? null
        : `This coverage needs ${demand} man-hours a day; ${crewCount} crew can legally supply ${supply}. Add crew, reduce the minimum on deck, or shorten the covered window.`,
  }
}

// ============================================================
// SEED TEMPLATES — Ladi's proven rosters (M/Y Thunder), verbatim.
// ============================================================
export const SEED_TEMPLATES = [
  {
    id: 'port-18h',
    name: 'In Port — 18h coverage',
    scenario: 'port',
    minOnDeck: 3,
    coverageWindow: [8, 21],   // 3-on-deck applies 08:00-21:00
    note: '5 crew, at least 3 constantly on deck through the covered day',
    roles: [
      { role: 'CAPTAIN',     cells: 'RRRRRRRRWWWWWWWRRWWWWWRR' },
      { role: 'CH.ENGINEER', cells: 'RRRRRRRRWWWWWWWWWWWWWRRR' },
      { role: 'CH.OFFICER',  cells: 'WRRRRRRRRWWWWWWWWWRRRWWW' },
      { role: 'BOSUN',       cells: 'RRRRRRRWWWWWWWRRWWWWWWRR' },
      { role: 'DECKHAND',    cells: 'RRRRRWWWWWWWRRWWWWWWRRRR' },
    ],
  },
  {
    id: 'anchor-24h',
    name: 'At Anchor — 24h coverage',
    scenario: 'anchor',
    minOnDeck: 1,
    note: '5 crew, round-the-clock coverage',
    captainVariants: [
      { name: 'Night watch 01-04 + guests from 11:00', cells: 'RWWWRRRRRRRWWWWWWWWWRRRR', note: '12W/12R - watch kept, 7h protected rest, evening rest wraps midnight. Fully compliant.' },
      { name: 'Night watch 01-04 + guests from 10:00', cells: 'RWWWRRRRRRWWWWWWWWWWRRRR', note: '13W/11R - same shape, one more guest hour. Compliant, zero weekly margin.' },
      { name: 'No night watch - guest mornings', cells: 'RRRRRRRWWWWWWWRRRWWWWWRR', note: '12W/12R - protected overnight rest, on deck 07:00 for guests.' },
    ],
    roles: [
      { role: 'CAPTAIN',     cells: 'RWWWRRRRRRRWWWWWWWWWRRRR' },
      { role: 'CH.ENGINEER', cells: 'RRRRRRRWWWWWWWWWWWWWRRRR' },
      { role: 'CH.OFFICER',  cells: 'WRRRRRRRRWWWWWWWWWRRRWWW' },
      { role: 'BOSUN',       cells: 'RRRRRRWWWWWWWRRWWWWWWRRR' },
      { role: 'DECKHAND',    cells: 'RRRRWWWWWWWRRWWWWWWRRRRR' },
    ],
  },
]

// ============================================================
// REBALANCE SOLVER — strategy-driven. Hours-neutral Work<->Rest
// swaps, every candidate validated by checkRow, never drops a
// covered hour below minimum. Proposes; never applies.
// ============================================================
function solveGaps(rows, minOnDeck, coverageWindow, opts = {}) {
  const { exclude = [], candidateOrder = 'adjacent' } = opts
  const req = normaliseMin(minOnDeck, coverageWindow)
  const cur = rows.map(r => ({ ...r }))
  const changes = []
  const unsolved = []
  const prev = h => (h + 23) % 24
  const next = h => (h + 1) % 24
  const usedIdx = new Set()

  for (let guard = 0; guard < 96; guard++) {
    const cov = coverage(cur)
    const gapHour = [...Array(24).keys()].find(h =>
      req[h] > 0 && cov[h] < req[h] && !unsolved.some(u => u.hour === h))
    if (gapHour === undefined) break

    const adjScore = x => (x.r.cells[prev(gapHour)] === 'W' || x.r.cells[next(gapHour)] === 'W') ? 0 : 1
    const restOf = x => x.r.cells.split('').filter(c => c === 'R').length
    let cands = cur.map((r, i) => ({ r, i }))
      .filter(x => x.r.cells[gapHour] === 'R'
        && exclude.indexOf(x.r.role) === -1 && exclude.indexOf(x.r.crew_name) === -1)

    if (candidateOrder === 'concentrate')
      cands.sort((a, b) => ((usedIdx.has(a.i) ? 0 : 1) - (usedIdx.has(b.i) ? 0 : 1)) || (adjScore(a) - adjScore(b)))
    else if (candidateOrder === 'spread')
      cands.sort((a, b) => ((usedIdx.has(a.i) ? 1 : 0) - (usedIdx.has(b.i) ? 1 : 0)) || (adjScore(a) - adjScore(b)))
    else if (candidateOrder === 'headroom')
      cands.sort((a, b) => (restOf(b) - restOf(a)) || (adjScore(a) - adjScore(b)))
    else
      cands.sort((a, b) => adjScore(a) - adjScore(b))

    let applied = false
    let reason = 'no eligible crew is resting at that hour'
    for (const { r, i } of cands) {
      const drops = [...Array(24).keys()]
        .filter(g => r.cells[g] === 'W' && (cov[g] - 1 >= req[g]))
        .sort((a, b) => {
          const adjR = g => (r.cells[prev(g)] === 'R' || r.cells[next(g)] === 'R') ? 0 : 1
          const d = adjR(a) - adjR(b)
          return d !== 0 ? d : cov[b] - cov[a]
        })
      if (drops.length) reason = 'every legal swap would break the rest-period structure (max 2 periods, one >=6h)'
      for (const g of drops) {
        const arr = r.cells.split('')
        arr[gapHour] = 'W'; arr[g] = 'R'
        const trial = arr.join('')
        if (checkRow(trial).issues.some(x => x.level === 'red')) continue
        cur[i] = { ...cur[i], cells: trial }
        changes.push({ rowIndex: i, who: r.crew_name || r.role, addHour: gapHour, dropHour: g })
        usedIdx.add(i)
        applied = true
        break
      }
      if (applied) break
    }
    if (!applied) unsolved.push({ hour: gapHour, reason })
  }
  return { changes, unsolved, rows: cur }
}

export function rebalanceOptions(rows, minOnDeck = 0, coverageWindow = null, opts = {}) {
  const capt = rows.find(r => /captain/i.test(r.role || ''))
  const protect = opts.protect || (capt ? [capt.role] : [])
  const strategies = [
    { id: 'single',   name: 'One person covers it', order: 'concentrate',
      note: 'The gap lands on a single crew member - one day changes, everyone else untouched.' },
    { id: 'spread',   name: 'Share it out',         order: 'spread',
      note: 'Gap hours split across different crew - smallest change per person.' },
    { id: 'headroom', name: 'Most rest first',      order: 'headroom',
      note: 'Loads whoever currently has the most rest margin.' },
  ]
  if (protect.length) strategies.push({ id: 'protect', name: 'Protect ' + protect.join(', '), order: 'adjacent',
    exclude: protect, note: protect.join(', ') + ' untouched - the rest of the crew absorbs the gap.' })

  const options = []
  const failed = []
  const seen = new Set()
  for (const st of strategies) {
    const res = solveGaps(rows, minOnDeck, coverageWindow, { candidateOrder: st.order, exclude: st.exclude || [] })
    if (res.unsolved.length || !res.changes.length) {
      failed.push({ strategy: st.name,
        reason: res.unsolved.map(u => String(u.hour).padStart(2, '0') + ':00 - ' + u.reason).join('; ') || 'no changes possible' })
      continue
    }
    const key = res.rows.map(r => r.cells).join('|')
    if (seen.has(key)) continue
    seen.add(key)
    const affected = [...new Set(res.changes.map(c => c.rowIndex))].map(i => {
      const c = checkRow(res.rows[i].cells)
      return { who: res.rows[i].crew_name || res.rows[i].role, restHours: c.restHours,
               periods: c.periods.length, margin: c.restHours - 11 }
    })
    options.push({ id: st.id, name: st.name, note: st.note, changes: res.changes, rows: res.rows, affected })
  }
  return { options, failed,
    note: options.length ? null
      : 'No legal rebalance exists with this crew. ' + failed.map(f => f.strategy + ': ' + f.reason).join(' | ') }
}

export function rebalance(rows, minOnDeck = 0, coverageWindow = null) {
  const r = rebalanceOptions(rows, minOnDeck, coverageWindow)
  const first = r.options[0]
  return first
    ? { ok: true, changes: first.changes, unsolved: [], rows: first.rows, note: null }
    : { ok: false, changes: [], unsolved: [], rows, note: r.note }
}
